/**
 * The store Worker's client (D165): buy this game, or (D165c) the pass —
 * come back here — and the reading of the page the browser comes back to.
 *
 * @remarks
 * Checkout is Stripe's page, not ours: the Worker answers a URL and the
 * browser goes there, and Stripe sends it back to the page it left with
 * the outcome on the query — `?purchase=completed` or `canceled` for a
 * game, `pass_completed` or `pass_canceled` for the pass. The shell reads
 * that once, says what happened in the chrome's voice, and drops it from
 * the address bar — the same arrival pattern as a magic link (D157
 * addendum). The entitlement itself arrives by webhook, so a fast return
 * can beat it by a moment; the turn that was refused is simply typed again.
 *
 * @packageDocumentation
 */

import {
	type CheckoutAnswer,
	checkoutAnswerSchema,
	type CheckoutRequest,
	checkoutRequestSchema,
	type CreatorAnswer,
	creatorAnswerSchema,
	type OnboardAnswer,
	onboardAnswerSchema,
	type OnboardRequest,
	type PassCheckoutRequest,
	type PassDescription,
	passDescriptionSchema,
	type PassHolding,
	passHoldingSchema,
	type PassPortalRequest,
	STORE_CHECKOUT_PATH,
	STORE_CREATOR_PATH,
	STORE_ONBOARD_PATH,
	STORE_PASS_CHECKOUT_PATH,
	STORE_PASS_HOLDING_PATH,
	STORE_PASS_PATH,
	STORE_PASS_PORTAL_PATH,
	type StoreApiError,
	storeApiErrorSchema,
} from '@orpheus/api-contracts';

/** What the browser came back with, when it came back from a checkout — a game's, or (D165c) the pass's. */
export type PurchaseArrival = 'canceled' | 'completed' | 'pass_canceled' | 'pass_completed';

const PURCHASE_ARRIVALS: readonly PurchaseArrival[] = ['canceled', 'completed', 'pass_canceled', 'pass_completed'];

const isPurchaseArrival = (value: string): value is PurchaseArrival => (PURCHASE_ARRIVALS as readonly string[]).includes(value);

/** How a checkout failed to start — the Worker's name for it, or the network's absence of one. */
export type CheckoutRefusal = StoreApiError['error'] | 'unreachable';

export type CheckoutOutcome = { readonly kind: 'refused'; readonly refusal: CheckoutRefusal } | { readonly kind: 'started'; readonly url: string };

/** What the browser came back with, when it came back from Stripe's onboarding — or the ask to start it (D165b). */
export type CreatorArrival = 'onboard' | 'refresh' | 'returned';

export interface StoreClient {
	/** The signed-in account as a creator (D165b): their connected account and whether it can be paid; `null` when they are not one. */
	creator(): Promise<CreatorAnswer | { readonly refusal: CheckoutRefusal }>;
	/** What the pass costs (D165c), off Stripe's own numbers — or why the store would not say (`pass_not_offered` on a deployment with none). */
	pass(): Promise<PassDescription | { readonly refusal: CheckoutRefusal }>;
	/** The signed-in account's own pass (D166): held, ending, lapsed, or `null` for one that never held it — or why not. */
	passHolding(): Promise<PassHolding | { readonly refusal: CheckoutRefusal }>;
	/** Start a checkout for the game; the answer is the page to go to, or why not. */
	startCheckout(gameId: string, returnUrl: string): Promise<CheckoutOutcome>;
	/** Become a creator, or resume becoming one (D165b): Stripe's onboarding page, or why not. */
	startOnboarding(returnUrl: string): Promise<CheckoutOutcome>;
	/** Subscribe to the pass (D165c): Stripe's checkout page, or why not. */
	startPassCheckout(returnUrl: string): Promise<CheckoutOutcome>;
	/** Manage the pass (D166): Stripe's Customer Portal — cancel, resume, the card, the invoices — or why not. */
	startPassPortal(returnUrl: string): Promise<CheckoutOutcome>;
}

export interface StoreClientOptions {
	readonly baseUrl: string;
	readonly fetchImplementation?: typeof fetch;
}

/**
 * The client.
 *
 * @param options - Where the Worker answers, and the network
 * @returns The client
 */
export const createStoreClient = (options: StoreClientOptions): StoreClient => {
	const fetchImplementation = options.fetchImplementation ?? fetch.bind(globalThis);

	/** One call to a door, with the cookie along: the body when it answered, or the refusal by name. */
	const call = async (path: string, body?: unknown): Promise<{ readonly answer: unknown } | { readonly refusal: CheckoutRefusal }> => {
		try {
			const response = await fetchImplementation(`${options.baseUrl}${path}`, {
				...(body === undefined ? {} : { body: JSON.stringify(body) }),
				credentials: 'include',
				headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
				method: body === undefined ? 'GET' : 'POST',
			});
			const answer: unknown = await response.json().catch(() => undefined);

			if (response.ok) {
				return { answer };
			}

			const refusal = storeApiErrorSchema.safeParse(answer);

			return { refusal: refusal.success ? refusal.data.error : 'unreachable' };
		} catch {
			return { refusal: 'unreachable' };
		}
	};

	/** A door that answers a page to go to. */
	const pageFrom = async (path: string, request: unknown, parse: (answer: unknown) => { readonly url: string }): Promise<CheckoutOutcome> => {
		const outcome = await call(path, request);

		return 'refusal' in outcome ? { kind: 'refused', refusal: outcome.refusal } : { kind: 'started', url: parse(outcome.answer).url };
	};

	return {
		creator: async () => {
			const outcome = await call(STORE_CREATOR_PATH);

			return 'refusal' in outcome ? { refusal: outcome.refusal } : creatorAnswerSchema.parse(outcome.answer);
		},
		pass: async () => {
			const outcome = await call(STORE_PASS_PATH);

			return 'refusal' in outcome ? { refusal: outcome.refusal } : passDescriptionSchema.parse(outcome.answer);
		},
		passHolding: async () => {
			const outcome = await call(STORE_PASS_HOLDING_PATH);

			return 'refusal' in outcome ? { refusal: outcome.refusal } : passHoldingSchema.parse(outcome.answer);
		},
		startCheckout: async (gameId, returnUrl) => {
			// Parsed on the way out as well as in: a game id the door would refuse is refused here, before the round trip.
			const request: CheckoutRequest = checkoutRequestSchema.parse({ gameId, returnUrl });

			return pageFrom(STORE_CHECKOUT_PATH, request, (answer): CheckoutAnswer => checkoutAnswerSchema.parse(answer));
		},
		startOnboarding: async (returnUrl) => {
			const request: OnboardRequest = { returnUrl };

			return pageFrom(STORE_ONBOARD_PATH, request, (answer): OnboardAnswer => onboardAnswerSchema.parse(answer));
		},
		startPassCheckout: async (returnUrl) => {
			const request: PassCheckoutRequest = { returnUrl };

			return pageFrom(STORE_PASS_CHECKOUT_PATH, request, (answer): CheckoutAnswer => checkoutAnswerSchema.parse(answer));
		},
		startPassPortal: async (returnUrl) => {
			const request: PassPortalRequest = { returnUrl };

			return pageFrom(STORE_PASS_PORTAL_PATH, request, (answer): CheckoutAnswer => checkoutAnswerSchema.parse(answer));
		},
	};
};

/**
 * What a page's query says about creator onboarding (D165b): the ask to
 * start it, or what Stripe sent the browser back with.
 *
 * @param search - `window.location.search`
 * @returns The arrival, or nothing
 */
export const readCreatorArrival = (search: string): CreatorArrival | undefined => {
	const outcome = new URLSearchParams(search).get('creator');

	return outcome === 'onboard' || outcome === 'returned' || outcome === 'refresh' ? outcome : undefined;
};

/**
 * What a page's query says about a checkout the browser came back from.
 *
 * @param search - `window.location.search`
 * @returns The arrival, or nothing when the page was not come back to
 */
export const readPurchaseArrival = (search: string): PurchaseArrival | undefined => {
	const outcome = new URLSearchParams(search).get('purchase');

	return outcome !== null && isPurchaseArrival(outcome) ? outcome : undefined;
};
